// frontend/packages/business-console/src/components/inbox/MessageBubble.jsx

import React from "react";
import { Box, Paper, Typography } from "@mui/material";

import { formatRelative } from "./relativeTime";

/**
 * Plain-language label for common Twilio SMS error codes. Used when
 * Twilio's ErrorMessage is missing or empty -- otherwise the Twilio
 * ErrorMessage is already operator-readable and we render that
 * verbatim.
 *
 * Full reference: https://www.twilio.com/docs/api/errors
 */
const TWILIO_ERROR_LABELS = {
  21610: "Recipient has opted out (STOPped)",
  30001: "Twilio queue overflow",
  30002: "Twilio account suspended",
  30003: "Unreachable destination handset",
  30004: "Message blocked by recipient",
  30005: "Unknown destination handset",
  30006: "Landline or unreachable carrier",
  30007: "Message filtered by carrier",
  30008: "Unknown error",
  30009: "Missing inbound segment",
  30010: "Message price exceeds max price",
};

function describeFailure({ errorCode, errorMessage }) {
  const code = String(errorCode || "").trim();
  const message = String(errorMessage || "").trim();
  // Prefer Twilio's own ErrorMessage when present -- it's already
  // operator-readable and may include more context than our label.
  if (message) return code ? `${message} (${code})` : message;
  if (code) {
    const label = TWILIO_ERROR_LABELS[code];
    return label ? `${label} (${code})` : `Twilio error ${code}`;
  }
  return "";
}

/**
 * Single message in the conversation thread. Inbound messages align left
 * with neutral background; outbound (replies from the business user) align
 * right with the brand color. Failed sends show a warning chip + when
 * available a Twilio-derived reason.
 */
export default function MessageBubble({ message }) {
  const m = message || {};
  const outbound = m.direction === "outbound";
  const failed = m.status === "failed";
  const delivered = m.status === "delivered";
  const author =
    m.senderName ||
    m.senderEmail ||
    (outbound ? "You" : "Website Visitor");

  // Built only when the Message has a real failure detail; otherwise
  // we fall back to the existing "failed to send" caption.
  const failureDetail = failed
    ? describeFailure({
        errorCode: m.errorCode,
        errorMessage: m.errorMessage,
      })
    : "";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: outbound ? "flex-end" : "flex-start",
        my: 1.25,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: "text.secondary", mb: 0.5, px: 1 }}
      >
        {author}
        {m.channel === "sms" ? " · SMS" : ""}
        {" · "}
        {formatRelative(m.createdAt)}
        {failed ? " · failed to send" : ""}
        {/* "delivered" shown only on outbound SMS where it's a real
            signal (email Messages stay "sent" because we don't track
            email delivery receipts yet). */}
        {delivered && outbound && m.channel === "sms" ? " · delivered" : ""}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          maxWidth: { xs: "85%", md: "70%" },
          px: 2,
          py: 1.25,
          borderRadius: 2,
          backgroundColor: outbound
            ? failed
              ? "error.light"
              : "primary.main"
            : "background.paper",
          color: outbound ? "primary.contrastText" : "text.primary",
          border: outbound ? "none" : 1,
          borderColor: "divider",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {m.subject && !outbound && (
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, mb: 0.5, opacity: 0.85 }}
          >
            {m.subject}
          </Typography>
        )}
        <Typography variant="body2">{m.bodyText}</Typography>
      </Paper>
      {failureDetail && (
        <Typography
          variant="caption"
          sx={{
            color: "error.main",
            mt: 0.5,
            px: 1,
            maxWidth: { xs: "85%", md: "70%" },
            textAlign: outbound ? "right" : "left",
          }}
        >
          Failure: {failureDetail}
        </Typography>
      )}
    </Box>
  );
}
